/** Lets application overlays consume history traversal without losing Forward entries. */
export interface BrowserHistoryNavigationDetail {
  direction: 'back' | 'forward'
  delta: number
  href: string
  /** An overlay has closed: consume its reserved entry rather than restoring it. */
  releaseBackEntry?: boolean
}

export class BrowserHistoryNavigation {
  private readonly _session = `${Date.now()}-${Math.random()}`
  private _index = 0
  private _reservedIndex: number | null = null
  public restoring = false
  private _restored: (() => void) | null = null
  private _approvedIndex: number | null = null
  private _confirming = false

  /** Create the in-page Back entry during the user's overlay-opening action. */
  reserveBackEntry() {
    this.write({ href: this._window.location.href }, this._window.location.href)
    this._reservedIndex = this._index
  }

  constructor(
    private readonly _window: Window,
    private readonly _dispatch: (detail: BrowserHistoryNavigationDetail) => boolean,
    private readonly _confirmNavigation?: () => Promise<boolean> | null,
  ) {}

  write(state: object, href: string, replace = false) {
    if (!replace) {
      this._index += 1
    }
    const nextState = {
      ...this._window.history.state,
      ...state,
      aeppicHistory: { session: this._session, index: this._index },
    }
    if (replace) {
      this._window.history.replaceState(nextState, null, href)
    } else {
      this._window.history.pushState(nextState, null, href)
    }
  }

  intercept(state: any): boolean {
    if (this.restoring) {
      this.restoring = false
      this._restored?.()
      this._restored = null
      return true
    }
    const position = state?.aeppicHistory
    if (position?.session !== this._session || !Number.isInteger(position.index)) {
      return false
    }
    const delta = position.index - this._index
    const approved = this._approvedIndex === position.index
    if (approved) this._approvedIndex = null
    if (delta !== 0 && !approved && this._confirming) {
      this.restoring = true
      this._window.history.go(-delta)
      return true
    }

    const confirmation = delta !== 0 && !approved ? this._confirmNavigation?.() : null
    if (confirmation) {
      this._confirming = true
      const restored = new Promise<void>(resolve => { this._restored = resolve })
      this.restoring = true
      this._window.history.go(-delta)
      void Promise.all([confirmation, restored]).then(([leave]) => {
        if (!leave) return
        this._approvedIndex = position.index
        this._window.history.go(delta)
      }).catch(error => console.error('Navigation confirmation failed', error)).finally(() => {
        this._confirming = false
      })
      return true
    }
    const detail: BrowserHistoryNavigationDetail = { direction: delta < 0 ? 'back' : 'forward', delta, href: state.href }
    if (delta !== 0 && !this._dispatch(detail)) {
      if (detail.releaseBackEntry && delta === -1 && this._reservedIndex === this._index) {
        this._index = position.index
        this._reservedIndex = null
        return true
      }
      // popstate is not cancelable: undo traversal instead of pushing a new
      // entry, which would discard the user's Forward history.
      this.restoring = true
      this._window.history.go(-delta)
      return true
    }
    this._index = position.index
    return false
  }
}
